import React, { useContext, useEffect, useState } from 'react'

import { mpfWhoHttp } from '../../assets/ts/api/mpf-who-http'
import PageOverlayBusy from '../../components/page-overlay/page-overlay-busy'
import useInquiries from '../../hooks/inquiry/use-inquiries'
import useInquiryCount from '../../hooks/inquiry/use-inquiry-count'
import { MetadataContext } from '../../store/metadata-context-provider'
import {
  ALL_SELECT_LABEL,
  ALL_SELECT_VALUE,
} from '../constants/global-constants'

const TestExample = () => {
  const metadata = useContext(MetadataContext)

  const [selectedComponentCode, setSelectedComponentCode] =
    useState(ALL_SELECT_VALUE)

  const [selectedSectionCode, setSelectedSectionCode] =
    useState(ALL_SELECT_VALUE)

  const [selectedCategoryCode, setSelectedCategoryCode] =
    useState(ALL_SELECT_VALUE)

  const [page, setPage] = useState(0)

  const [personSearch, setPersonSearch] = useState('')
  const [personResults, setPersonResults] = useState<
    { id: string; name: string }[]
  >([])

  const [selectedPerson, setSelectedPerson] = useState<{
    id: string
    name: string
  } | null>()

  useEffect(() => {
    const query = async () => {
      if (personSearch?.length < 3) {
        setPersonResults([])
      } else {
        const people = await mpfWhoHttp().Picker(personSearch)
        setPersonResults(
          [...people].map((p) => ({
            id: p.rid,
            name: p.persona,
          }))
        )
      }
    }

    query()
  }, [personSearch])

  const args = {
    assignedComponentCodeFilter: selectedComponentCode,
    categoryCodeFilter: selectedCategoryCode,
    relatedUserFilter: selectedPerson?.id ?? null,
    sectionCodeFilter: selectedSectionCode,
  }
  const argsWithPaging = {
    ...args,
    pageNumber: page,
  }
  const { inquiries, inquiriesAreLoading } = useInquiries(argsWithPaging)
  const { inquiryCountIsLoading, inquiryCountValue } = useInquiryCount(args)

  const components = metadata.components.map((c) => (
    <option
      key={c.ComponentCode}
      label={c.Component}
      value={c.ComponentCode}
    ></option>
  ))

  const sections = metadata.sections.map((s) => (
    <option
      key={s.SectionCode}
      label={s.Section}
      value={s.SectionCode}
    ></option>
  ))

  const categories = metadata.categories
    .filter(
      (c) =>
        selectedSectionCode === ALL_SELECT_VALUE ||
        c.ParentSectionCode === selectedSectionCode
    )
    .map((c) => (
      <option
        key={c.CategoryCode}
        label={c.Category}
        value={c.CategoryCode}
      ></option>
    ))

  const ALL = (
    <option label={ALL_SELECT_LABEL} value={ALL_SELECT_VALUE}></option>
  )

  const handleClear = () => {
    setSelectedComponentCode(ALL_SELECT_VALUE)
    setSelectedSectionCode(ALL_SELECT_VALUE)
    setSelectedCategoryCode(ALL_SELECT_VALUE)
    setPersonSearch('')
    setPersonResults([])
    setSelectedPerson(null)
    setPage(0)
  }

  const handleComponentChange = (e) => {
    setSelectedComponentCode(parseInt(e.target.value))
    setPage(0)
  }

  const handleSectionChange = (e) => {
    setSelectedSectionCode(parseInt(e.target.value))
    setSelectedCategoryCode(ALL_SELECT_VALUE)
    setPage(0)
  }

  const handleCategoryChange = (e) => {
    setSelectedCategoryCode(parseInt(e.target.value))
    setPage(0)
  }

  const handlePersonChange = (p) => {
    setSelectedPerson(p)
    setPersonSearch('')
    setPersonResults([])
  }

  const changePage = (delta) => {
    setPage((currentPage) => {
      const newPage = currentPage + delta

      return Math.max(newPage, 0)
    })
  }

  const buttonClass = 'm-1 p-1 active'

  const busy =
    metadata.isLoading || inquiriesAreLoading || inquiryCountIsLoading

  return (
    <>
      {busy && <PageOverlayBusy />}
      <main>
        <table>
          <thead>
            <tr>
              <td colSpan={4}>
                <span>Total: {inquiryCountValue}</span>

                <button className={buttonClass} onClick={handleClear}>
                  Clear
                </button>
                <button className={buttonClass} onClick={() => changePage(-1)}>
                  -
                </button>
                <div
                  className='text-center'
                  style={{ display: 'inline-block', width: '30px' }}
                >
                  {page}
                </div>
                <button className={buttonClass} onClick={() => changePage(+1)}>
                  +
                </button>
                <input
                  onChange={(e) => setPersonSearch(e.target.value)}
                  placeholder='Person Search...'
                  value={personSearch}
                ></input>

                {personResults.map((p) => (
                  <button
                    className={buttonClass}
                    key={p.id}
                    onClick={() => handlePersonChange(p)}
                  >
                    {p.name}
                  </button>
                ))}

                {selectedPerson && (
                  <span
                    className='border-radius3 px-2 py-1 font-12 poppins-semibold'
                    style={{ borderColor: 'black', borderStyle: 'solid' }}
                  >
                    {selectedPerson?.name}
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <th>ID</th>
              <th>Component</th>
              <th>Section</th>
              <th>Category</th>
            </tr>
            <tr>
              <td></td>
              <td>
                <select
                  onChange={handleComponentChange}
                  value={selectedComponentCode}
                >
                  {[ALL, ...components]}
                </select>
              </td>
              <td>
                <select
                  onChange={handleSectionChange}
                  value={selectedSectionCode}
                >
                  {[ALL, ...sections]}
                </select>
              </td>
              <td>
                <select
                  onChange={handleCategoryChange}
                  value={selectedCategoryCode}
                >
                  {[ALL, ...categories]}
                </select>
              </td>
            </tr>
          </thead>
          <tbody>
            {inquiries.map((i) => (
              <tr key={i.InquiryGUID}>
                <td>{i.InquiryID}</td>
                <td>{i['AssignedComponent/MetadataValue']}</td>
                <td>{i['Section/MetadataValue']}</td>
                <td>{i['Category/MetadataValue']}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </main>
    </>
  )
}

export default TestExample
